import React, { useEffect, useState } from 'react';

import { EmptyState } from '~/shared-components';
import { useTranslation } from 'react-i18next';
import { Idea as IdeaType } from '~/content/types';
import { useAppDispatch } from '~/store';
import {
  addNotification,
  addPermanentNotification,
} from '~/notifications-provider/notificationsSlice';
import { Idea } from '../Idea';
import { useIdeasLoader } from './useIdeasLoader';
import { useDeleteIdea } from '../hooks/useDeleteIdea';
import { DeleteConfirmationModal } from '../modals/DeleteConfirmationModal';

import { LoadingScreen } from './LoadingScreen';
import { MasonryStyled } from './styles';

type ListProps = {
  onCreateNewIdea: () => void;
  onEditIdea: (idea: IdeaType) => void;
};

export const List = ({
  onCreateNewIdea,
  onEditIdea,
}: ListProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { ideas, loadMore, loading } = useIdeasLoader();
  const { t } = useTranslation();
  const { deleteIdea, deleteIdeaData, deleteIdeaError } = useDeleteIdea();

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState<boolean>(false);
  const [ideaToDelete, setIdeaToDelete] = useState<IdeaType | null>(null);

  const onDelete = (idea) => {
    setShowDeleteConfirmationModal(true);
    setIdeaToDelete(idea);
  };

  const onConfirmDelete = () => {
    setShowDeleteConfirmationModal(false);
    deleteIdea(ideaToDelete?.id);
  };

  const onCancelDelete = () => {
    setShowDeleteConfirmationModal(false);
    setIdeaToDelete(null);
  };

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    const error =
      deleteIdeaError ||
      (deleteIdeaData && !deleteIdeaData.ideasRemove.success);
    if (deleteIdeaData && deleteIdeaData.ideasRemove.success) {
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.deleteIdea.successMessage')}`
        )
      );
    } else if (error) {
      dispatch(
        addPermanentNotification(
          `⚠️ ${t('content.notifications.deleteIdea.errorMessage')}`
        )
      );
    }
  }, [deleteIdeaData, deleteIdeaError, dispatch, t]);

  if (loading) {
    return <LoadingScreen />;
  }
  if (!ideas.length) {
    return (
      <EmptyState
        title={t('content.emptyIdeas.title')}
        subtitle={t('content.emptyIdeas.subtitle')}
        heroImg="https://buffer-publish.s3.amazonaws.com/images/content-empty-ideas.svg"
        primaryAction={{
          label: t('content.common.createIdea'),
          onClick: onCreateNewIdea,
        }}
      />
    );
  }
  return (
    <>
      <MasonryStyled
        breakpointCols={{
          default: 5,
          1448: 4,
          1164: 3,
          880: 2,
          596: 1,
        }}
        className="ideas-masonry-grid"
        columnClassName="ideas-masonry-column"
      >
        {ideas.map((idea) => (
          <Idea
            key={idea.id}
            idea={idea}
            onClick={onEditIdea}
            onDelete={onDelete}
          />
        ))}
      </MasonryStyled>
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      )}
    </>
  );
};
